import * as React from 'react';
import {
  MISAFIR_TALEPLERI,
  paraEtiketi,
  secenekEtiketi,
  talepMetni,
  type MisafirTalepDurum,
  type MisafirTalepTipi,
} from '@hotelcms/shared';
import { ApiHatasi, api, oturum } from '@/lib/api';
import { anMs } from '@/lib/utils';

/**
 * Kat hizmetleri ekrani.
 *
 * Tasarim kaynagi: Claude Design — "otelcms Kat Hizmetleri.dc.html".
 * Tasarimdaki oda listesi ve ilerleme yuzdesi ureticiydi; buradaki her sey
 * gercek veriden gelir:
 *   GET   /api/housekeeping/gorevler          -> odalar, gorevliler, misafirler
 *   PATCH /api/housekeeping/gorevler/:id      -> durum degistir
 *   POST  /api/housekeeping/odalar/:id/temiz  -> kontrol onayi
 *
 * Canli akis, Durable Object yayinindan (WebSocket) beslenir: kat gorevlisinin
 * telefonundan yaptigi degisiklik burada aninda gorunur.
 */

interface Gorev {
  id: string;
  roomId: string;
  odaNo: string | null;
  kat: string | null;
  odaTipi: string | null;
  durum: 'BEKLIYOR' | 'DEVAM' | 'BITTI' | 'KONTROL_BEKLIYOR' | 'IPTAL';
  aciklama: string | null;
  baslangic: string | number | null;
  odaDurumu: 'BOS' | 'DOLU' | 'ARIZALI' | 'BLOKE';
  arizaNotu: string | null;
  temizlikDurumu: 'TEMIZ' | 'KIRLI' | 'TEMIZLENIYOR' | 'KONTROL_BEKLIYOR';
  gorevliAd: string | null;
  misafir: { ad: string; not: string } | null;
  /** Misafirin telefonundan actigi "rahatsiz etmeyin" bitis ani (ms) */
  rahatsizEtmeyin: number | null;
  /** Onaylanmis gec cikis saati - oda o saate kadar temizlenmez */
  gecCikisSaati: string | null;
}

/** Ekrandaki dort renkli durum + sistemdeki "kontrol bekliyor" adimi. */
type Durum = 'KIRLI' | 'TEMIZLENIYOR' | 'KONTROL' | 'HAZIR' | 'ARIZALI';

const DURUM_BILGI: Record<Durum, { etiket: string; renk: string; hapZemin: string; hapYazi: string; ipucu: string }> = {
  KIRLI: { etiket: 'Kirli', renk: '#e5484d', hapZemin: '#fdeceb', hapYazi: '#c0392b', ipucu: 'Dokunun: temizliğe başla' },
  TEMIZLENIYOR: { etiket: 'Temizleniyor', renk: '#f2b632', hapZemin: '#fff5dc', hapYazi: '#b9840f', ipucu: 'Dokunun: bitti işaretle' },
  KONTROL: { etiket: 'Kontrol bekliyor', renk: '#1f4fd8', hapZemin: '#edf2ff', hapYazi: '#1f4fd8', ipucu: 'Dokunun: onayla' },
  HAZIR: { etiket: 'Hazır', renk: '#12a150', hapZemin: '#e6f7ec', hapYazi: '#0f7a3d', ipucu: 'Dokunun: kirli işaretle' },
  ARIZALI: { etiket: 'Arızalı', renk: '#5b6480', hapZemin: '#eef1f8', hapYazi: '#5b6480', ipucu: 'Teknik servis bekliyor' },
};

/**
 * Dokunmanin ne yapacagi. Onay penceresi bunu kullaniciya once soyler:
 * kat gorevlisi telefonu cebinde tasirken yanlislikla bir odayi "hazir"
 * isaretlemesin diye.
 */
const GECIS: Record<Durum, { sonraki: Durum; eylem: string; soru: string } | null> = {
  KIRLI: { sonraki: 'TEMIZLENIYOR', eylem: 'Temizliğe başla', soru: 'Bu odanın temizliğine başlıyor musunuz?' },
  TEMIZLENIYOR: { sonraki: 'KONTROL', eylem: 'Bitti işaretle', soru: 'Temizlik bitti mi? Oda kontrole gönderilecek.' },
  KONTROL: { sonraki: 'HAZIR', eylem: 'Hazır olarak onayla', soru: 'Oda kontrol edildi ve satışa hazır mı?' },
  HAZIR: { sonraki: 'KIRLI', eylem: 'Kirli işaretle', soru: 'Bu oda yeniden kirli olarak işaretlensin mi?' },
  ARIZALI: null,
};

const GOREVLI_RENK = ['#1f4fd8', '#7c3aed', '#0f7a3d', '#b9840f', '#0891b2'];

function durumBul(g: Gorev): Durum {
  if (g.odaDurumu === 'ARIZALI' || g.odaDurumu === 'BLOKE') return 'ARIZALI';
  if (g.temizlikDurumu === 'KIRLI') return 'KIRLI';
  if (g.temizlikDurumu === 'TEMIZLENIYOR') return 'TEMIZLENIYOR';
  if (g.temizlikDurumu === 'KONTROL_BEKLIYOR') return 'KONTROL';
  return 'HAZIR';
}

const saat = (an: number | Date = Date.now()) =>
  new Date(an).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

/** Misafirin odadaki QR'dan actigi talep (bkz. features/guest/MisafirMobil). */
interface MisafirTalep {
  id: string;
  odaNo: string;
  tip: MisafirTalepTipi;
  /** Katalog KODU; etiket burada cozulur */
  secenek: string;
  ekip: string;
  durum: MisafirTalepDurum;
  ucretKurus: number;
  puan: number | null;
  an: number;
}

/**
 * Yeni talep sesi.
 *
 * Ses DOSYASI yok: Web Audio ile iki kisa nota uretilir. Boylece ek bir
 * varlik indirilmez ve ses tarayici otomatik-oynatma engeline takilmaz
 * (kullanici zaten sayfayla etkilesmis olur). Ses acilamiyorsa sessizce
 * gecilir - bildirim ikincil, liste zaten guncelleniyor.
 */
function zilCal() {
  try {
    const Ses = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ses) return;
    const ctx = new Ses();
    const simdi = ctx.currentTime;
    for (const [gecikme, frekans] of [[0, 880], [0.14, 1175]] as const) {
      const osc = ctx.createOscillator();
      const kazanc = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frekans;
      kazanc.gain.setValueAtTime(0.0001, simdi + gecikme);
      kazanc.gain.exponentialRampToValueAtTime(0.12, simdi + gecikme + 0.01);
      kazanc.gain.exponentialRampToValueAtTime(0.0001, simdi + gecikme + 0.13);
      osc.connect(kazanc).connect(ctx.destination);
      osc.start(simdi + gecikme);
      osc.stop(simdi + gecikme + 0.14);
    }
    setTimeout(() => void ctx.close().catch(() => {}), 600);
  } catch {
    /* ses kapali ya da desteklenmiyor */
  }
}

const EKIP_RENK: Record<string, string> = {
  KAT: '#1f4fd8',
  MUTFAK: '#b9840f',
  RESEPSIYON: '#7c3aed',
  TEKNIK: '#c0392b',
};

export function KatHizmetleri({ tenantId, yetkiler }: { tenantId: string; yetkiler: string[] }) {
  const yazabilir = yetkiler.includes('housekeeping:write');
  const [gorevler, setGorevler] = React.useState<Gorev[]>([]);
  const [kat, setKat] = React.useState<string>('hepsi');
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [islenen, setIslenen] = React.useState<string | null>(null);
  const [akis, setAkis] = React.useState<{ t: string; m: string }[]>([]);
  const [onay, setOnay] = React.useState<Gorev | null>(null);
  const [talepler, setTalepler] = React.useState<MisafirTalep[]>([]);
  const [kapsam, setKapsam] = React.useState<'acik' | 'hepsi'>('acik');

  const talepleriYukle = React.useCallback(async () => {
    const yol = kapsam === 'hepsi' ? '/api/misafir-talepleri?kapsam=hepsi' : '/api/misafir-talepleri';
    const veri = await api<{ talepler: MisafirTalep[] }>(yol).catch(() => null);
    if (veri) setTalepler(veri.talepler);
  }, [kapsam]);

  const yukle = React.useCallback(async () => {
    try {
      const veri = await api<{ gorevler: Gorev[] }>('/api/housekeeping/gorevler');
      setGorevler(veri.gorevler);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Görevler alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
    void talepleriYukle();
  }, [yukle, talepleriYukle]);

  // Canli yayin: baska bir cihazdan yapilan degisiklik aninda dusar
  React.useEffect(() => {
    const token = oturum.token();
    if (!token) return;
    const ws = new WebSocket(
      `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/${tenantId}?token=${token}`,
    );
    ws.onmessage = (olay) => {
      const mesaj = JSON.parse(olay.data as string) as {
        tip: string;
        roomId?: string;
        durum?: string;
        odaNo?: string;
        talepTipi?: string;
      };
      // Misafirin odadaki QR'dan actigi talep: tasarimin "aynı anda" vaadi.
      if (mesaj.tip === 'MISAFIR_TALEBI') {
        void talepleriYukle();
        // Temizlik/ariza talebi kat hizmetleri gorevi de acar; liste tazelensin.
        void yukle();
        const tip = mesaj.talepTipi as MisafirTalepTipi;
        const ad = MISAFIR_TALEPLERI[tip] ? talepMetni(tip).baslik : 'Misafir talebi';
        setAkis((o) => [{ t: saat(), m: `Oda ${mesaj.odaNo ?? '—'} · ${ad}` }, ...o].slice(0, 8));
        // Yeni talep DUYULUR olmali: personel ekrana bakmiyor olabilir.
        if (mesaj.durum === 'ALINDI') zilCal();
        return;
      }
      if (mesaj.tip !== 'HOUSEKEEPING_GUNCELLENDI' && mesaj.tip !== 'ODA_GUNCELLENDI') return;
      void yukle();
      setAkis((o) => [{ t: saat(), m: `Oda durumu güncellendi` }, ...o].slice(0, 8));
    };
    const ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [tenantId, yukle, talepleriYukle]);

  async function ilerlet(g: Gorev) {
    if (!yazabilir || islenen) return;
    const durum = durumBul(g);
    if (durum === 'ARIZALI') return;
    // Sunucu da reddeder; burada durdurmak gereksiz bir istekten kurtarir.
    if (durum === 'KIRLI' && g.rahatsizEtmeyin && g.rahatsizEtmeyin > Date.now()) {
      setHata('Misafir "rahatsız etmeyin" işaretlemiş. Kaldırılana kadar odaya girilmez.');
      return;
    }

    setIslenen(g.id);
    try {
      if (durum === 'KONTROL') {
        await api(`/api/housekeeping/odalar/${g.roomId}/temiz`, { method: 'POST' });
      } else {
        const yeni = durum === 'KIRLI' ? 'DEVAM' : durum === 'TEMIZLENIYOR' ? 'BITTI' : 'IPTAL';
        await api(`/api/housekeeping/gorevler/${g.id}`, { method: 'PATCH', body: JSON.stringify({ durum: yeni }) });
      }
      const sonraki: Record<Durum, string> = {
        KIRLI: 'temizliğe başlandı',
        TEMIZLENIYOR: 'temizlik bitti, kontrol bekliyor',
        KONTROL: 'hazır olarak onaylandı',
        HAZIR: 'kirli olarak işaretlendi',
        ARIZALI: '',
      };
      setAkis((o) => [{ t: saat(), m: `${g.odaNo} · ${sonraki[durum]}` }, ...o].slice(0, 8));
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Durum değiştirilemedi.');
    } finally {
      setIslenen(null);
    }
  }

  const katlar = [...new Set(gorevler.map((g) => g.kat).filter(Boolean) as string[])].sort();
  /**
   * Siralama isin kendisine gore: once yapilacak olan.
   * (Sunucudaki `oncelik` alani artan siralandiginda dolu odalar en sona
   * dusuyordu; kat gorevlisi icin dogru sira bu.)
   */
  const ACILIYET: Record<Durum, number> = { KIRLI: 0, TEMIZLENIYOR: 1, KONTROL: 2, HAZIR: 3, ARIZALI: 4 };
  const gosterilen = gorevler
    .filter((g) => kat === 'hepsi' || g.kat === kat)
    .slice()
    .sort(
      (a, b) =>
        ACILIYET[durumBul(a)] - ACILIYET[durumBul(b)] ||
        (a.odaNo ?? '').localeCompare(b.odaNo ?? '', 'tr', { numeric: true }),
    );
  const say = (d: Durum) => gorevler.filter((g) => durumBul(g) === d).length;
  const toplam = gorevler.length || 1;

  const gorevliler = [...new Set(gorevler.map((g) => g.gorevliAd).filter(Boolean) as string[])].map((ad, i) => {
    const benim = gorevler.filter((g) => g.gorevliAd === ad);
    const aktif = benim.find((g) => durumBul(g) === 'TEMIZLENIYOR');
    return {
      ad,
      bas: ad.slice(0, 1).toUpperCase(),
      renk: GOREVLI_RENK[i % GOREVLI_RENK.length]!,
      is: aktif ? `Oda ${aktif.odaNo} temizleniyor` : 'Boşta',
      tamam: `${benim.filter((g) => durumBul(g) === 'HAZIR').length}/${benim.length}`,
    };
  });

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Kat hizmetleri</h1>
        {katlar.length > 1 && (
          <div className="flex w-full gap-1 rounded-full border border-cizgi bg-white p-1 sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {[['hepsi', 'Tüm katlar'] as const, ...katlar.map((k) => [k, `${k}. Kat`] as const)].map(([k, etiket]) => (
              <button
                key={k}
                onClick={() => setKat(k)}
                className={
                  'whitespace-nowrap rounded-full px-4 py-[7px] text-[13px] font-semibold transition-colors ' +
                  (kat === k ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
                }
              >
                {etiket}
              </button>
            ))}
          </div>
        )}
        <span className="ml-2 flex items-center gap-2 text-[13px] text-gri">
          <span className="relative size-2">
            <span className="absolute inset-0 rounded-full bg-marka-600" />
            <span className="absolute inset-0 rounded-full border-[1.5px] border-marka-600 [animation:ring_1.8s_ease-out_infinite]" />
          </span>
          Değişiklikler anında resepsiyonda görünür
        </span>
        <div className="ml-auto flex gap-4 text-[13px] font-medium text-gri">
          {(['KIRLI', 'TEMIZLENIYOR', 'KONTROL', 'HAZIR', 'ARIZALI'] as Durum[]).map((d) => (
            <span key={d} className="flex items-center gap-[7px]">
              <span className="size-3 rounded" style={{ background: DURUM_BILGI[d].renk }} />
              {DURUM_BILGI[d].etiket}
            </span>
          ))}
        </div>
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      <div className="grid gap-3 [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:grid-cols-2 xl:grid-cols-4">
        {(['KIRLI', 'TEMIZLENIYOR', 'KONTROL', 'HAZIR'] as Durum[]).map((d) => (
          <div key={d} className="flex flex-col gap-2 rounded-2xl border border-cizgi bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-[.08em] text-gri">{DURUM_BILGI[d].etiket}</span>
              <span className="size-2.5 rounded-[3px]" style={{ background: DURUM_BILGI[d].renk }} />
            </div>
            <span className="text-[28px] font-bold leading-none tracking-[-.03em]">{say(d)}</span>
            <div className="h-1 overflow-hidden rounded-sm bg-satir">
              <div
                className="h-full origin-left"
                style={{
                  width: `${Math.round((say(d) / toplam) * 100)}%`,
                  background: DURUM_BILGI[d].renk,
                  animation: 'bar 1s .3s cubic-bezier(.2,.8,.2,1) both',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="grid items-start gap-[18px] xl:grid-cols-[minmax(0,1fr)_300px]">
        {yukleniyor ? (
          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-[18px] border border-cizgi bg-white" />
            ))}
          </div>
        ) : gosterilen.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-[20px] border border-dashed border-cizgi bg-white p-14 text-center">
            <span className="text-4xl">🧹</span>
            <h2 className="text-xl font-bold">Bugün için görev yok</h2>
            <p className="max-w-md text-sm text-gri">
              Görevler her gece 04:00'teki gece kapanışında oluşturulur.
            </p>
          </div>
        ) : (
          <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
            {gosterilen.map((g, i) => {
              const d = durumBul(g);
              const bilgi = DURUM_BILGI[d];
              const temizleniyor = d === 'TEMIZLENIYOR';
              const baslangicMs = anMs(g.baslangic);
              const gecen = temizleniyor && baslangicMs ? Math.max(0, Math.round((Date.now() - baslangicMs) / 60_000)) : null;
              return (
                <button
                  key={g.id}
                  onClick={() => setOnay(g)}
                  disabled={!yazabilir || d === 'ARIZALI' || islenen === g.id}
                  className="relative flex flex-col gap-3.5 overflow-hidden rounded-[18px] border border-cizgi bg-white p-[18px] text-left transition hover:-translate-y-[3px] hover:border-marka-600 hover:shadow-[0_22px_40px_-28px_rgba(11,21,48,.35)] disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:border-cizgi disabled:hover:shadow-none"
                  style={{ animation: 'popin .45s cubic-bezier(.2,.8,.2,1) both', animationDelay: `${i * 35}ms` }}
                >
                  <span className="absolute inset-x-0 top-0 h-1 transition-colors" style={{ background: bilgi.renk }} />
                  {temizleniyor && (
                    <span className="pointer-events-none absolute inset-0 overflow-hidden">
                      <span className="absolute inset-y-0 w-[45%] [animation:sweep_2.2s_ease-in-out_infinite] bg-[linear-gradient(90deg,transparent,rgba(242,182,50,.14),transparent)]" />
                    </span>
                  )}

                  <div className="relative flex items-start justify-between">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[22px] font-bold tracking-[-.02em]">{g.odaNo}</span>
                      <span className="text-xs text-gri">{g.odaTipi ?? '—'}</span>
                    </div>
                    <span
                      className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
                      style={{ background: bilgi.hapZemin, color: bilgi.hapYazi }}
                    >
                      {temizleniyor && (
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="[animation:wipe_1s_ease-in-out_infinite]">
                          <path d="M2 10l6-6M8 2l2 2" />
                        </svg>
                      )}
                      {bilgi.etiket}
                    </span>
                  </div>

                  {(g.rahatsizEtmeyin || g.gecCikisSaati) && (
                    <div className="relative flex flex-wrap gap-1.5">
                      {g.rahatsizEtmeyin && g.rahatsizEtmeyin > Date.now() && (
                        <span className="flex items-center gap-1.5 rounded-full bg-satir px-2.5 py-1 text-[11px] font-bold text-gri">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                            <path d="M18.36 5.64A9 9 0 105.64 18.36 9 9 0 0018.36 5.64zM5.64 5.64l12.72 12.72" />
                          </svg>
                          Rahatsız etmeyin
                        </span>
                      )}
                      {g.gecCikisSaati && (
                        <span className="rounded-full bg-[#f3e8ff] px-2.5 py-1 text-[11px] font-bold text-[#7c3aed]">
                          Geç çıkış {g.gecCikisSaati}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="relative flex flex-col gap-1.5 text-[13px] text-gri">
                    <div className="flex justify-between">
                      <span>Misafir</span>
                      <span className="font-semibold text-lacivert">
                        {g.misafir ? `${g.misafir.ad}${g.misafir.not ? ` · ${g.misafir.not}` : ''}` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Görevli</span>
                      <span className="font-semibold text-lacivert">{g.gorevliAd ?? 'Atanmadı'}</span>
                    </div>
                  </div>

                  <span className="relative text-xs text-[#8a93ad]">
                    {d === 'ARIZALI' && g.arizaNotu
                      ? g.arizaNotu
                      : islenen === g.id
                        ? 'Gönderiliyor…'
                        : gecen !== null
                          ? `${gecen} dakikadır temizlikte · ${bilgi.ipucu}`
                          : yazabilir
                            ? bilgi.ipucu
                            : bilgi.etiket}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <aside className="flex flex-col gap-3.5 [animation:rise_.6s_.15s_cubic-bezier(.2,.7,.2,1)_both] xl:sticky xl:top-[92px]">
          <MisafirTalepleri
            yazabilir={yazabilir}
            talepler={talepler}
            kapsam={kapsam}
            onKapsam={setKapsam}
            onDegisti={talepleriYukle}
          />

          <div className="relative flex flex-col gap-3.5 overflow-hidden rounded-[20px] bg-lacivert p-5 text-white">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />
            <h3 className="relative text-[15px] font-bold">Görevliler</h3>
            {gorevliler.length === 0 ? (
              <p className="relative text-[13px] text-[#b6c2e2]">
                Görevlere henüz kimse atanmamış. Atama yapılınca burada görünür.
              </p>
            ) : (
              gorevliler.map((g) => (
                <div key={g.ad} className="relative flex items-center gap-3">
                  <span
                    className="relative grid size-9 shrink-0 place-items-center rounded-full text-[13px] font-bold"
                    style={{ background: g.renk }}
                  >
                    {g.bas}
                    <span className="absolute -bottom-px -right-px size-2.5 rounded-full border-2 border-lacivert bg-[#5fd48f]" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-sm font-semibold">{g.ad}</span>
                    <span className="text-xs text-[#b6c2e2]">{g.is}</span>
                  </div>
                  <span className="text-[13px] font-bold text-mavi-acik">{g.tamam}</span>
                </div>
              ))
            )}
          </div>

          <div className="flex flex-col gap-3 rounded-[20px] border border-cizgi bg-white p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold">Canlı akış</h3>
              <span className="size-2 rounded-full bg-yesil [animation:blink_1.4s_infinite]" />
            </div>
            {akis.length === 0 ? (
              <p className="text-[13px] text-gri">Henüz hareket yok.</p>
            ) : (
              akis.map((l, i) => (
                <div key={i} className="flex gap-2.5 text-[13px] [animation:rise_.4s_both]">
                  <span className="shrink-0 tabular-nums text-gri">{l.t}</span>
                  <span className="text-lacivert">{l.m}</span>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>

      {onay && (
        <OnayPenceresi
          gorev={onay}
          islemde={islenen === onay.id}
          onVazgec={() => setOnay(null)}
          onOnayla={async () => {
            const g = onay;
            setOnay(null);
            await ilerlet(g);
          }}
        />
      )}
    </main>
  );
}

/**
 * Misafir talepleri paneli.
 *
 * Tasarim kaynagi: "otelcms Misafir Mobil.dc.html" sagdaki "Resepsiyon
 * ekranında · aynı anda" karti. Misafir odadaki QR'dan talep acinca kayit
 * buraya WebSocket ile aninda duser; personel iki dokunusla ilerletir ve
 * misafirin telefonundaki ilerleme cubugu ayni anda hareket eder.
 */
function MisafirTalepleri({
  talepler,
  yazabilir,
  kapsam,
  onKapsam,
  onDegisti,
}: {
  talepler: MisafirTalep[];
  yazabilir: boolean;
  kapsam: 'acik' | 'hepsi';
  onKapsam: (k: 'acik' | 'hepsi') => void;
  onDegisti: () => void | Promise<void>;
}) {
  const [islenen, setIslenen] = React.useState<string | null>(null);
  const [hata, setHata] = React.useState<string | null>(null);

  async function degistir(t: MisafirTalep, durum: 'ISLEMDE' | 'TAMAMLANDI' | 'IPTAL') {
    if (!yazabilir || islenen) return;
    setIslenen(t.id);
    setHata(null);
    try {
      await api(`/api/misafir-talepleri/${t.id}`, { method: 'PATCH', body: JSON.stringify({ durum }) });
      await onDegisti();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Güncellenemedi.');
    } finally {
      setIslenen(null);
    }
  }

  const acikAdet = talepler.filter((t) => t.durum === 'ALINDI' || t.durum === 'ISLEMDE').length;

  return (
    <div className="flex flex-col gap-3 rounded-[20px] border border-cizgi bg-white p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[15px] font-bold">Misafir talepleri</h3>
        {acikAdet > 0 && (
          <span className="grid min-w-6 place-items-center rounded-full bg-sari px-2 py-0.5 text-[11px] font-bold text-lacivert">
            {acikAdet}
          </span>
        )}
        <div className="ml-auto flex gap-0.5 rounded-full bg-zemin p-0.5">
          {(
            [
              ['acik', 'Açık'],
              ['hepsi', 'Tümü'],
            ] as const
          ).map(([k, etiket]) => (
            <button
              key={k}
              onClick={() => onKapsam(k)}
              className={
                'rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ' +
                (kapsam === k ? 'bg-lacivert text-white' : 'text-gri')
              }
            >
              {etiket}
            </button>
          ))}
        </div>
      </div>

      {hata && <p className="text-[12.5px] text-red-600">{hata}</p>}

      {talepler.length === 0 ? (
        <p className="text-[13px] leading-relaxed text-gri">
          {kapsam === 'acik'
            ? "Açık talep yok. Misafir odadaki QR'ı okutup talep açınca burada anında belirir."
            : 'Henüz hiç talep gelmemiş.'}
        </p>
      ) : (
        talepler.map((t) => {
          const kapali = t.durum === 'TAMAMLANDI' || t.durum === 'IPTAL';
          const metin = talepMetni(t.tip);
          return (
            <div
              key={t.id}
              className={
                'flex flex-col gap-2 rounded-2xl bg-zemin p-3 [animation:popin_.35s_both] ' +
                (kapali ? 'opacity-60' : '')
              }
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="size-2 flex-none rounded-full"
                  style={{
                    background: EKIP_RENK[t.ekip] ?? '#5b6480',
                    animation: t.durum === 'ALINDI' ? 'blink 1.2s infinite' : undefined,
                  }}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[13.5px] font-semibold text-lacivert">
                    Oda {t.odaNo} · {metin.baslik}
                  </span>
                  <span className="truncate text-xs text-gri">
                    {secenekEtiketi(t.tip, t.secenek)}
                    {t.ucretKurus > 0 && ` · ${paraEtiketi(t.ucretKurus)} folyoda`}
                    {' · '}
                    {t.durum === 'ALINDI'
                      ? 'yeni'
                      : t.durum === 'ISLEMDE'
                        ? metin.ortaAdim
                        : t.durum === 'TAMAMLANDI'
                          ? 'tamamlandı'
                          : 'iptal'}
                  </span>
                </div>
                {t.puan ? (
                  <span className="flex-none text-[11px] font-bold text-sari">{t.puan}★</span>
                ) : (
                  <span className="flex-none tabular-nums text-[11px] text-gri">{saat(t.an)}</span>
                )}
              </div>

              {yazabilir && !kapali && (
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => void degistir(t, t.durum === 'ALINDI' ? 'ISLEMDE' : 'TAMAMLANDI')}
                    disabled={islenen === t.id}
                    className="rounded-full border-[1.5px] border-cizgi bg-white px-3 py-1.5 text-xs font-semibold text-lacivert transition hover:border-lacivert disabled:opacity-60"
                  >
                    {islenen === t.id
                      ? 'Gönderiliyor…'
                      : t.durum === 'ALINDI'
                        ? 'İşleme al'
                        : t.tip === 'GEC_CIKIS'
                          ? 'Onayla'
                          : 'Tamamlandı işaretle'}
                  </button>
                  <button
                    onClick={() => void degistir(t, 'IPTAL')}
                    disabled={islenen === t.id}
                    className="rounded-full px-3 py-1.5 text-xs font-semibold text-gri transition hover:text-red-600 disabled:opacity-60"
                  >
                    {t.tip === 'GEC_CIKIS' ? 'Reddet' : 'İptal'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

/**
 * "Emin misiniz?" penceresi.
 *
 * Kat gorevlisi bu ekrani telefondan, cogu zaman tek elle kullanir; yanlis
 * dokunus kolaydir. Pencere odanin numarasini ve gecisi buyuk puntoyla
 * gosterir, boylece onaylamadan once yanlis odaya bastigini fark eder.
 */
function OnayPenceresi({
  gorev,
  islemde,
  onVazgec,
  onOnayla,
}: {
  gorev: Gorev;
  islemde: boolean;
  onVazgec: () => void;
  onOnayla: () => void;
}) {
  const d = durumBul(gorev);
  const gecis = GECIS[d];

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onVazgec();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onVazgec]);

  if (!gecis) return null;
  const suanki = DURUM_BILGI[d];
  const hedef = DURUM_BILGI[gecis.sonraki];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4 [animation:fadein_.2s_both]"
      onClick={onVazgec}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onay-baslik"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[420px] rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
      >
        <span className="text-[13px] font-semibold text-gri">Oda {gorev.odaNo ?? '—'}</span>
        <h2 id="onay-baslik" className="mt-1 text-[20px] font-bold leading-snug tracking-[-.01em] text-lacivert">
          {gecis.soru}
        </h2>

        <div className="mt-5 flex items-center gap-3">
          <span
            className="rounded-full px-3 py-1.5 text-[12px] font-bold"
            style={{ background: suanki.hapZemin, color: suanki.hapYazi }}
          >
            {suanki.etiket}
          </span>
          <svg width="20" height="12" viewBox="0 0 20 12" fill="none" stroke="#7d879c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 6h16m-4-4 4 4-4 4" />
          </svg>
          <span
            className="rounded-full px-3 py-1.5 text-[12px] font-bold"
            style={{ background: hedef.hapZemin, color: hedef.hapYazi }}
          >
            {hedef.etiket}
          </span>
        </div>

        {gorev.misafir && (
          <p className="mt-4 rounded-xl bg-zemin px-3.5 py-3 text-[13px] text-gri">
            Odada misafir kayıtlı:{' '}
            <span className="font-semibold text-lacivert">{gorev.misafir.ad}</span>
            {gorev.misafir.not ? ` · ${gorev.misafir.not}` : ''}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onVazgec}
            className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
          >
            Vazgeç
          </button>
          <button
            type="button"
            autoFocus
            disabled={islemde}
            onClick={onOnayla}
            className="flex flex-1 items-center justify-center gap-2 rounded-full py-3 text-[15px] font-semibold text-white transition disabled:opacity-70"
            style={{ background: hedef.hapYazi }}
          >
            {islemde && (
              <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />
            )}
            {gecis.eylem}
          </button>
        </div>
      </div>
    </div>
  );
}
